import {Component, OnInit, computed, inject, input, output, signal} from '@angular/core';
import {AuthenticationService} from '../authentication.service';
import {UserHttpService} from '../../owner/x-http-requests/user-http.service';
import {UserDto} from '../../models/admin_owner/user/user-dto';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {AlertLevel} from '../../enums/alert-level';
import {describeAccountActionError} from '../../_shared/errors/account-action-errors';
import {SelectableContext, contextScope, contextTitle} from './context.models';

/**
 * **Impersonation, from a chosen tile of the context picker** — the other half of #279's move of the two
 * platform-owner capabilities out of `owner/user`.
 *
 * ### What it is, and what it deliberately is not
 *
 * ADR #260 §3a split one mechanism into two. *Configuring a shard as yourself* became context selection —
 * {@link SelfActivationPanelComponent} supplies the grant that makes a tile appear, and entering it is an
 * ordinary entry. *Reproducing what a specific person sees, with their exact roles and limits* stayed
 * impersonation, unchanged. This panel is that second half and nothing else: it mints no grant, and the session
 * it produces carries `impersonated_by`, shows the banner, refuses nesting and refuses a platform-owner target
 * exactly as before.
 *
 * ### Why it reuses `AuthenticationService.startImpersonation`
 *
 * That method is the single place the owner's session is stashed, the impersonation token activated, and
 * `impersonation_origin` written through `CookieService` (the global `localStorage.setItem` is trapped and
 * throws, so a second implementation would look correct and break on its first use). Reusing it is also the
 * point of this work: `owner/user` becomes removable because nothing was reimplemented beside it.
 *
 * ### Where the accounts come from
 *
 * `GET /management/User/List?company=&shard=`, the same read `owner/user` performs, gated
 * `DynamicAuthorize("Management", MetaRole.Owner)` — i.e. to exactly the sessions that may impersonate. It
 * lists management **`User`** rows; employees are not impersonation targets and never have been (the server's
 * target lookup reads the main-DB `User` table), so a company whose staff are all employees answers with an
 * empty list, which the panel says out loud rather than rendering as a blank.
 */
@Component({
  selector: 'auth-tile-impersonation-panel',
  standalone: true,
  host: {class: 'block'},
  template: `
    <section class="rounded-2xl border-[1px] border-gray-200 bg-white p-4"
             aria-labelledby="impersonation-heading">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 id="impersonation-heading" class="truncate text-base font-bold tracking-tight text-gray-900">
            Konto übernehmen
          </h2>
          <p class="mt-0.5 truncate text-sm text-gray-500">{{ title() }} · {{ scope() }}</p>
        </div>
        <button type="button" (click)="close.emit()" aria-label="Kontoauswahl schließen"
                class="shrink-0 rounded-2xl border-[1px] border-gray-200 px-3 py-1.5 text-sm font-semibold
                       text-gray-600 transition-all duration-200 hover:bg-gray-50 active:scale-95">
          Schließen
        </button>
      </div>

      <p class="mt-2 text-sm text-gray-500">
        Sie arbeiten dann als dieses Konto — mit dessen Rechten und Einschränkungen. Der Vorgang wird
        protokolliert.
      </p>

      @if (loading()) {
        <p class="py-6 text-center text-sm text-gray-500" role="status" aria-live="polite">
          Konten werden geladen…
        </p>
      } @else if (loadFailed()) {
        <p class="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          Die Konten dieses Bereichs konnten nicht geladen werden.
          <button type="button" (click)="load()" class="underline">Erneut versuchen</button>
        </p>
      } @else if (!accounts().length) {
        <p class="mt-3 rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-600">
          Hier gibt es kein Konto zum Übernehmen. Mitarbeitende lassen sich nicht übernehmen — nur
          Verwaltungskonten.
        </p>
      } @else {
        <label for="impersonation-search" class="sr-only">Konten durchsuchen</label>
        <input id="impersonation-search" type="search" [value]="search()"
               (input)="search.set(value($event))" placeholder="Konto suchen …"
               class="mt-3 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm
                      transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                      focus:ring-1 focus:ring-theme-primary-500">

        @if (!filtered().length) {
          <p class="mt-3 text-sm text-gray-500">Kein Konto passt zu Ihrer Suche.</p>
        }

        <ul class="mt-3 max-h-64 space-y-1 overflow-y-auto">
          @for (account of filtered(); track account.id) {
            <li class="rounded-xl border-[1px] border-gray-100 p-3">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="min-w-0 truncate text-sm font-bold text-gray-900">{{ account.name }}</span>
                @if (pending()?.id === account.id) {
                  <!-- A two-step confirm rendered IN the panel, not window.confirm: the native dialog cannot
                       be styled, cannot be read by a test through the DOM, and would be the one English
                       sentence left on a German screen — which is what owner/user still shows. -->
                  <span class="flex shrink-0 items-center gap-2">
                    <button type="button" (click)="pending.set(null)"
                            class="rounded-2xl border-[1px] border-gray-200 px-3 py-1.5 text-sm font-semibold
                                   text-gray-600 transition-all duration-200 hover:bg-gray-50 active:scale-95">
                      Abbrechen
                    </button>
                    <button type="button" (click)="confirm(account)" [disabled]="busy()"
                            class="rounded-2xl bg-red-600 px-3 py-1.5 text-sm font-semibold text-white
                                   shadow-lg transition-all duration-200 hover:bg-red-700 active:scale-95
                                   disabled:cursor-not-allowed disabled:opacity-30">
                      Wirklich übernehmen
                    </button>
                  </span>
                } @else {
                  <button type="button" (click)="pending.set(account)" [disabled]="busy()"
                          [attr.aria-label]="'Konto ' + account.name + ' übernehmen'"
                          class="shrink-0 rounded-2xl border-[1px] border-gray-200 px-3 py-1.5 text-sm
                                 font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-50
                                 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30">
                    Übernehmen
                  </button>
                }
              </div>
              @if (pending()?.id === account.id) {
                <p class="mt-2 text-sm text-gray-600" data-test="impersonation-confirm">
                  {{ confirmation(account) }}
                </p>
              }
            </li>
          }
        </ul>
      }
    </section>
  `
})
export class TileImpersonationPanelComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly users = inject(UserHttpService);
  private readonly alerts = inject(GlobalAlertService);

  /** The tile that was chosen. Its company and shard are the whole of what this panel lists accounts for. */
  readonly context = input.required<SelectableContext>();

  /** Asked to go away — the parent owns whether the panel is mounted. */
  readonly close = output<void>();

  protected readonly accounts = signal<UserDto[]>([]);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  protected readonly busy = signal(false);
  protected readonly search = signal('');

  /** The account whose "really?" step is on screen. One at a time, by construction. */
  protected readonly pending = signal<UserDto | null>(null);

  protected readonly filtered = computed(() => {
    const needle = this.search().trim().toLowerCase();
    if (!needle) return this.accounts();
    return this.accounts().filter(account => (account.name ?? '').toLowerCase().includes(needle));
  });

  /**
   * The first read happens here, not in the constructor: {@link context} is a `required` signal input, and
   * reading one during construction throws `NG0950` — the value is written by the framework only after the
   * instance exists. The failure is total (the component never renders), so it is not a subtlety worth
   * rediscovering.
   */
  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadFailed.set(false);

    // A company-scoped tile passes an empty shard, which the endpoint reads as "every shard of this company" —
    // the right answer for a tile that is itself company-wide.
    this.users.list(this.context().companyId ?? '', this.context().shardId ?? '').subscribe({
      next: accounts => {
        this.loading.set(false);
        this.accounts.set(accounts ?? []);
      },
      error: () => {
        this.loading.set(false);
        this.loadFailed.set(true);
      }
    });
  }

  protected confirm(account: UserDto): void {
    if (this.busy()) return;
    this.busy.set(true);

    this.auth.startImpersonation(account.id).subscribe({
      // A full document load, not a router navigation: the shell that renders is chosen from the session's
      // roles at bootstrap, and the impersonated session's roles can select a different shell entirely.
      next: () => window.open('/', '_self'),
      error: error => {
        this.busy.set(false);
        this.pending.set(null);
        // German, mapped from `ApiError.code` — never `description`, which is the server's developer-facing
        // English. The two refusals that matter here carry codes: E_IMPERSONATION_NESTED and
        // E_IMPERSONATION_PLATFORMOWNER_FAILED. Everything else this endpoint answers is a bare status with no
        // ApiError body at all, so the fallback is the sentence most readers will see.
        this.alerts.createAndShow('Übernahme nicht möglich',
          describeAccountActionError(error, 'Das Konto konnte nicht übernommen werden. Bitte versuchen Sie es '
            + 'erneut.'),
          AlertLevel.error, 8000);
      }
    });
  }

  /** What the "really?" step says: who, where, and that it is on the record. */
  protected confirmation(account: UserDto): string {
    return `Sie arbeiten danach als ${account.name} in ${this.title()}. Das wird protokolliert.`;
  }

  protected title(): string {
    return contextTitle(this.context());
  }

  protected scope(): string {
    return contextScope(this.context());
  }

  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}
